// * Can be used on both client and server side (but server side without Firebase ID token)

import { DEFAULT_FETCH_HEADERS } from '@/constants/api-fetcher';
import { useFirebaseAuthStore } from '@/stores/auth.store';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type FetchWrapperOptions = {
  params?: Record<string, string>;
  headers?: Record<string, string>;
  requestBody?: unknown;
};

export class ApiFetcherError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiFetcherError';
    this.status = status;
  }
}

function createApiUrl(endpoint: string, params: Record<string, string> = {}): string {
  const url = `${process.env.NEXT_PUBLIC_API_BASE_URL || process.env.API_BASE_URL}${endpoint}`;
  const searchParams = new URLSearchParams(params);
  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

async function getFirebaseAuthIdToken(): Promise<string | null> {
  if (typeof window === 'undefined') {
    // * Server-side: We can't get the Firebase ID token here
    // TODO: 有需要從 Next.js 的 server side 發 backend API 的話，再實裝 server-side session management
    return null;
  } else {
    // * Client-side
    let { firebaseAuthIdToken } = useFirebaseAuthStore.getState(); // * Sync access to Zustand store
    if (!firebaseAuthIdToken) {
      await new Promise<void>((resolve) => {
        const unsubscribe = useFirebaseAuthStore.subscribe(
          (state) => state.firebaseAuthIdToken, // * 選擇器：監聽 firebaseAuthIdToken 屬性
          (token) => {
            if (token) {
              firebaseAuthIdToken = token;
              unsubscribe(); // * 確保只執行一次
              resolve();
            }
          }
        );
      });
    }
    return firebaseAuthIdToken;
  }
}

async function fetchWrapper<T>(
  endpoint: string,
  method: HttpMethod,
  { params = {}, headers = {}, requestBody }: FetchWrapperOptions = {}
): Promise<T> {
  const apiUrl = createApiUrl(endpoint, params);
  const firebaseAuthIdToken = await getFirebaseAuthIdToken();
  const options: RequestInit = {
    method,
    headers: { ...DEFAULT_FETCH_HEADERS, ...headers },
  };

  if (firebaseAuthIdToken) {
    (options.headers as Record<string, string>)[`Authorization`] = `Bearer ${firebaseAuthIdToken}`;
  }
  if (requestBody) {
    options.body = JSON.stringify(requestBody);
  }
  try {
    const response = await fetch(apiUrl.toString(), options);
    const json = await response.json();
    if (!response.ok) {
      console.log(`API fetcher error: ${json.message || 'None.'}`);
      throw new ApiFetcherError(
        response.status,
        `An error occurred. Please try again later, or contact your admin for help.`
      );
    }
    return json;
  } catch (error) {
    if (error instanceof ApiFetcherError) {
      throw error;
    }
    throw new ApiFetcherError(500, 'Network error or unexpected error occurred');
  }
}

const apiFetcher = {
  get: async <T>(endpoint: string, options?: FetchWrapperOptions) =>
    fetchWrapper<T>(endpoint, 'GET', options),

  post: async <T>(endpoint: string, options?: FetchWrapperOptions) =>
    fetchWrapper<T>(endpoint, 'POST', options),

  put: async <T>(endpoint: string, options?: FetchWrapperOptions) =>
    fetchWrapper<T>(endpoint, 'PUT', options),

  patch: async <T>(endpoint: string, options?: FetchWrapperOptions) =>
    fetchWrapper<T>(endpoint, 'PATCH', options),

  delete: async <T>(endpoint: string, options?: FetchWrapperOptions) =>
    fetchWrapper<T>(endpoint, 'DELETE', options),
};

export default apiFetcher;
